import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from "recharts";
import {
	AlertTriangle,
	ArrowRight,
	CheckCircle2,
	FileText,
	Lightbulb,
	Loader2,
	Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { useGenerateInsights } from "@/hooks/transcripts/useTranscriptMutations";
import { getInsightLevel, safeExtractExplanation, safeExtractValue } from "./utils";
import type { InsightField, Transcript, TranscriptInsights } from "@/types/transcript";

export interface TranscriptInsightsTabProps {
	transcript: Transcript;
}

interface SemiGaugeProps {
	value: number;
	label: string;
	size?: number;
}

/** Semicircle gauge; severity is conveyed by the level label, not by color. */
const SemiGauge: React.FC<SemiGaugeProps> = ({ value, label, size = 80 }) => {
	const clamped = Math.min(Math.max(value, 0), 1);
	const radius = (size - 8) / 2;
	const circumference = Math.PI * radius;
	const offset = circumference * (1 - clamped);
	const cx = size / 2;
	const cy = size / 2 + 4;

	return (
		<svg
			width={size}
			height={size / 2 + 12}
			viewBox={`0 0 ${size} ${size / 2 + 12}`}
			role="img"
			aria-label={`${label}: ${Math.round(clamped * 100)}%`}
		>
			<path
				d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
				fill="none"
				stroke="hsl(var(--muted))"
				strokeWidth={6}
			/>
			<path
				d={`M ${cx - radius} ${cy} A ${radius} ${radius} 0 0 1 ${cx + radius} ${cy}`}
				fill="none"
				stroke="hsl(var(--primary))"
				strokeWidth={6}
				strokeLinecap="round"
				strokeDasharray={circumference}
				strokeDashoffset={offset}
			/>
			<text x={cx} y={cy - 8} textAnchor="middle" className="fill-foreground text-xs font-semibold">
				{label}
			</text>
		</svg>
	);
};

/** AI insights tab: overall gauge, radar assessment, scores and badge lists. */
export const TranscriptInsightsTab: React.FC<TranscriptInsightsTabProps> = ({ transcript }) => {
	const { t } = useTranslation();
	const generateMutation = useGenerateInsights();
	const isGenerating = generateMutation.isPending;
	const insights: TranscriptInsights | null = transcript.insights;

	const levelLabel = (level: "high" | "medium" | "low") =>
		({
			high: t("transcripts.insights.levelHigh", "High"),
			medium: t("transcripts.insights.levelMedium", "Medium"),
			low: t("transcripts.insights.levelLow", "Low"),
		})[level];

	const radarData = useMemo(() => {
		if (!insights)
			return [];
		const fields = [
			{ key: "communication_skills", label: t("transcripts.insights.communicationSkills", "Communication") },
			{ key: "engagement_level", label: t("transcripts.insights.engagementLevel", "Engagement") },
			{ key: "confidence", label: t("transcripts.insights.confidence", "Confidence") },
			{ key: "technical_depth", label: t("transcripts.insights.technicalDepth", "Technical") },
			{ key: "cultural_fit", label: t("transcripts.insights.culturalFit", "Culture Fit") },
			{ key: "motivation", label: t("transcripts.insights.motivation", "Motivation") },
		];
		return fields
			.map((field) => {
				const value = safeExtractValue(insights[field.key]);
				return typeof value === "number" ? { subject: field.label, value: Math.round(value * 100) } : null;
			})
			.filter(Boolean) as Array<{ subject: string; value: number }>;
	}, [insights, t]);

	const renderGaugeCard = (label: string, field: InsightField | undefined) => {
		const value = safeExtractValue(field);
		const explanation = safeExtractExplanation(field);
		if (typeof value !== "number")
			return null;
		const level = getInsightLevel(value);
		return (
			<div className="flex flex-col items-center gap-1 rounded-sm border border-border bg-card p-3">
				<SemiGauge value={value} label={levelLabel(level)} />
				<span className={cn("text-xs font-semibold", level === "low" ? "text-destructive" : "text-foreground")}>
					{label}
				</span>
				{explanation && (
					<p className="mt-1 text-center text-[11px] leading-tight text-muted-foreground">{explanation}</p>
				)}
			</div>
		);
	};

	const renderBadgeList = (
		label: string,
		field: InsightField | undefined,
		icon: React.ReactNode,
		iconClassName: string
	) => {
		const items = safeExtractValue(field, []);
		const explanation = safeExtractExplanation(field);
		if (!Array.isArray(items) || items.length === 0)
			return null;
		return (
			<div className="space-y-2">
				<div className="flex items-center gap-2">
					<span className={iconClassName} aria-hidden>{icon}</span>
					<h4 className="text-sm font-semibold text-foreground">{label}</h4>
				</div>
				<div className="flex flex-wrap gap-1.5">
					{items.filter(Boolean).map((item) => (
						<Badge key={item} variant="secondary" className="text-xs">
							{item}
						</Badge>
					))}
				</div>
				{explanation && <p className="mt-1 text-xs text-muted-foreground">{explanation}</p>}
			</div>
		);
	};

	if (!insights || Object.keys(insights).length === 0)
		return (
			<div className="flex h-full flex-col items-center justify-center p-6">
				<EmptyState
					icon={Lightbulb}
					title={t("transcripts.noInsights", "No insights yet")}
					description={t(
						"transcripts.noInsightsDescription",
						"Extract structured candidate insights from this conversation."
					)}
				/>
				<Button
					size="sm"
					className="mt-2 gap-2"
					onClick={() => generateMutation.mutate(transcript.id)}
					disabled={isGenerating || !transcript.transcript_content}
				>
					{isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />}
					{isGenerating
						? t("transcripts.generating", "Generating…")
						: t("transcripts.generateInsights", "Generate Insights")}
				</Button>
			</div>
		);

	return (
		<div className="relative h-full min-h-0">
			<div className="absolute inset-0 overflow-y-auto scrollbar-thin p-4">
				<div className="space-y-6">
					{insights.overall_impression && (
						<div className="flex items-center gap-4 rounded-sm border border-border bg-muted/30 p-4">
							<SemiGauge
								value={
									typeof safeExtractValue(insights.overall_impression, 0) === "number"
										? (safeExtractValue(insights.overall_impression, 0) as number)
										: 0
								}
								label={levelLabel(
									getInsightLevel(
										typeof safeExtractValue(insights.overall_impression, 0) === "number"
											? (safeExtractValue(insights.overall_impression, 0) as number)
											: 0
									)
								)}
								size={96}
							/>
							<div className="min-w-0 flex-1">
								<h3 className="text-sm font-semibold text-foreground">
									{t("transcripts.insights.overallImpression", "Overall Impression")}
								</h3>
								<p className="mt-1 text-sm text-muted-foreground">
									{safeExtractExplanation(insights.overall_impression)}
								</p>
							</div>
						</div>
					)}

					{radarData.length >= 3 && (
						<div className="rounded-sm border border-border bg-card p-4">
							<h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
								{t("transcripts.insights.assessment", "Assessment")}
							</h3>
							<div className="h-[220px]">
								<ResponsiveContainer width="100%" height="100%">
									<RadarChart data={radarData} cx="50%" cy="50%" outerRadius="75%">
										<PolarGrid strokeDasharray="3 3" />
										<PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
										<Radar
											dataKey="value"
											stroke="hsl(var(--primary))"
											fill="hsl(var(--primary))"
											fillOpacity={0.15}
											strokeWidth={2}
										/>
									</RadarChart>
								</ResponsiveContainer>
							</div>
						</div>
					)}

					<div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
						{renderGaugeCard(t("transcripts.insights.communicationSkills", "Communication"), insights.communication_skills)}
						{renderGaugeCard(t("transcripts.insights.engagementLevel", "Engagement"), insights.engagement_level)}
						{renderGaugeCard(t("transcripts.insights.confidence", "Confidence"), insights.confidence)}
						{renderGaugeCard(t("transcripts.insights.technicalDepth", "Technical"), insights.technical_depth)}
						{renderGaugeCard(t("transcripts.insights.culturalFit", "Culture Fit"), insights.cultural_fit)}
						{renderGaugeCard(t("transcripts.insights.motivation", "Motivation"), insights.motivation)}
					</div>

					<div className="space-y-4 border-t border-border pt-4">
						{renderBadgeList(
							t("transcripts.insights.topicsDiscussed", "Topics Discussed"),
							insights.topics_discussed,
							<FileText className="h-3.5 w-3.5" />,
							"text-muted-foreground"
						)}
						{renderBadgeList(
							t("transcripts.insights.skillsMentioned", "Skills Mentioned"),
							insights.skills_mentioned,
							<CheckCircle2 className="h-3.5 w-3.5" />,
							"text-muted-foreground"
						)}
						{renderBadgeList(
							t("transcripts.insights.strengths", "Strengths"),
							insights.strengths,
							<Sparkles className="h-3.5 w-3.5" />,
							"text-muted-foreground"
						)}
						{renderBadgeList(
							t("transcripts.insights.redFlags", "Red Flags"),
							insights.red_flags,
							<AlertTriangle className="h-3.5 w-3.5" />,
							"text-destructive"
						)}
						{renderBadgeList(
							t("transcripts.insights.nextSteps", "Next Steps"),
							insights.recommended_next_steps,
							<ArrowRight className="h-3.5 w-3.5" />,
							"text-muted-foreground"
						)}
					</div>

					<div className="border-t border-border pt-4">
						<Button
							variant="outline"
							size="sm"
							className="gap-2"
							onClick={() => generateMutation.mutate(transcript.id)}
							disabled={isGenerating}
						>
							{isGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lightbulb className="h-4 w-4" />}
							{isGenerating
								? t("transcripts.regenerating", "Regenerating…")
								: t("transcripts.regenerateInsights", "Regenerate Insights")}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
